import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Business, LocationOn, AutoAwesome, Settings, LinkOff } from '@mui/icons-material';
import { get, post, put } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import GoogleConnect from '../components/GoogleConnect';
import LocationsList from '../components/LocationsList';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AuthProps, Location, SubscriptionStatus } from '../types';

function Dashboard({ signOut, user }: AuthProps) {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    // Check if user just connected
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');
    
    if (connected) {
      handleGoogleConnect();
    } else if (error) {
      setError('Failed to connect to Google Business Profile');
    } else {
      // Load existing locations on page load
      loadExistingLocations();
    }
    
    // Load subscription status
    loadSubscriptionStatus();
  }, [searchParams]);

  const loadExistingLocations = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response: any = await makeAuthenticatedRequest('/businesses');
      const data: any = await response.body.json();
      
      if (data.locations && data.locations.length > 0) {
        setLocations(data.locations);
        setIsConnected(true);
      }
    } catch (err) {
      console.error('Error loading existing locations:', err);
      // Don't show error for initial load - user might not have connected yet
    } finally {
      setLoading(false);
    }
  };

  const makeAuthenticatedRequest = async (path: string, options: any = {}) => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (options.method === 'POST') {
        return await post({
          apiName: 'reviewReplyApi',
          path: path,
          options: {
            ...options,
            headers: {
              'Authorization': `Bearer ${token}`,
              ...options.headers
            }
          }
        }).response;
      } else if (options.method === 'PUT') {
        return await put({
          apiName: 'reviewReplyApi',
          path: path,
          options: {
            ...options,
            headers: {
              'Authorization': `Bearer ${token}`,
              ...options.headers
            }
          }
        }).response;
      } else {
        return await get({
          apiName: 'reviewReplyApi',
          path: path,
          options: {
            ...options,
            headers: {
              'Authorization': `Bearer ${token}`,
              ...options.headers
            }
          }
        }).response;
      }
    } catch (error) {
      console.error('Auth request failed:', error);
      throw error;
    }
  };

  const handleGoogleConnect = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Get locations after connecting
      const response: any = await makeAuthenticatedRequest('/businesses');
      const data: any = await response.body.json();
      setLocations(data.locations || []);
      setIsConnected(true);
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError('Failed to fetch locations: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const refreshLocations = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response: any = await makeAuthenticatedRequest('/businesses?refresh=true');
      const data: any = await response.body.json();
      setLocations(data.locations || []);
    } catch (err) {
      setError('Failed to refresh locations: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptionStatus = async () => {
    try {
      console.log('Loading subscription status...');
      const response: any = await makeAuthenticatedRequest('/subscription');
      const data: any = await response.body.json();
      console.log('Subscription status loaded:', data);
      setSubscriptionStatus(data);
    } catch (err) {
      console.error('Error loading subscription status:', err);
      // Set empty status so UI shows "start trial" buttons
      setSubscriptionStatus({ has_subscription: false });
    }
  };

  const startFreeTrial = async (planType) => {
    try {
      const response: any = await makeAuthenticatedRequest('/subscription', {
        method: 'POST',
        body: JSON.stringify({ plan_type: planType }),
        headers: { 'Content-Type': 'application/json' }
      });
      const data: any = await response.body.json();
      
      if (response.status === 200) {
        console.log('Trial started:', data);
        alert(`${planType.toUpperCase()} trial started successfully! You have 30 days to try it out.`);
        loadSubscriptionStatus();
      } else if (data.error && data.error.includes('Trial period already used')) {
        alert('You have already used your free trial. Please add a payment method to subscribe.');
      } else {
        alert(data.error || 'Failed to start trial');
      }
    } catch (err) {
      console.error('Error starting trial:', err);
      alert('Failed to start trial. Please try again.');
    }
  };

  const handleAddPaymentMethod = async (planType) => {
    try {
      console.log('Creating Stripe setup session for plan:', planType);
      
      const response: any = await makeAuthenticatedRequest('/stripe/setup-session', {
        method: 'POST',
        body: JSON.stringify({ plan_type: planType }),
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data: any = await response.body.json();
      
      if (data.setup_url) {
        // Redirect to Stripe Checkout
        window.location.href = data.setup_url;
      } else {
        throw new Error('No setup URL received');
      }
      
    } catch (err) {
      console.error('Error adding payment method:', err);
      alert('Failed to start payment setup. Please try again.');
    }
  };

  const handleLocationUpdate = () => {
    loadExistingLocations();
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Google Business Profile? All auto-replies will be disabled. You will need to reconnect to resume service.')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://l1xj0vaiy0.execute-api.us-east-1.amazonaws.com/prod'}/businesses`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setIsConnected(false);
        setLocations([]);
        alert('Google Business account disconnected successfully');
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to disconnect');
      }
    } catch (err) {
      console.error('Error disconnecting:', err);
      setError('Failed to disconnect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mr: 2 }}>
            ReplyMojo
          </Typography>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {user?.attributes?.email || user?.signInDetails?.loginId || user?.username}
          </Typography>
          <Button 
            color="inherit"
            startIcon={<Settings />}
            onClick={() => navigate('/app/settings')}
            sx={{ mr: 1 }}
          >
            Settings
          </Button>
          <Button color="inherit" onClick={signOut}>
            Sign Out
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Subscription Status Card */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Subscription Status
                </Typography>
                {subscriptionStatus ? (
                  <Box>
                    {subscriptionStatus.has_subscription && subscriptionStatus.status !== 'cancelled' ? (
                      <Box>
                        <Chip 
                          label={`${subscriptionStatus.status.toUpperCase()} - ${subscriptionStatus.plan_type.toUpperCase()} Plan`}
                          color={subscriptionStatus.status === 'trial' ? 'warning' : 'success'}
                          sx={{ mb: 2 }}
                        />
                        
                        {subscriptionStatus.status === 'trial' && (
                          <Box sx={{ mb: 2 }}>
                            <Alert 
                              severity={subscriptionStatus.days_remaining <= 7 ? 'warning' : 'info'}
                              sx={{ mb: 2 }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {subscriptionStatus.has_payment_method 
                                  ? `Trial active for ${subscriptionStatus.days_remaining} more days. Billing starts automatically after trial ends.`
                                  : `${subscriptionStatus.days_remaining} days left in your free trial`
                                }
                              </Typography>
                              {!subscriptionStatus.has_payment_method && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                  Add your payment method now to ensure uninterrupted service after trial ends.
                                </Typography>
                              )}
                            </Alert>
                            
                            {!subscriptionStatus.has_payment_method && (
                              <Button 
                                variant="contained" 
                                color="primary"
                                onClick={() => handleAddPaymentMethod(subscriptionStatus.plan_type)}
                                sx={{ mr: 2 }}
                              >
                                Add Payment Method
                              </Button>
                            )}
                          </Box>
                        )}
                        
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Locations: {subscriptionStatus.locations_limit} | 
                          Responses: {subscriptionStatus.responses_limit}/month
                        </Typography>
                      </Box>
                    ) : subscriptionStatus.status === 'cancelled' ? (
                      <Box>
                        <Chip 
                          label="CANCELLED SUBSCRIPTION"
                          color="error"
                          sx={{ mb: 2 }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Your subscription has been cancelled. Start a new subscription to continue using the service.
                        </Typography>
                        <Stack direction="row" spacing={2}>
                          <Button 
                            variant="contained" 
                            onClick={() => startFreeTrial('basic')}
                            size="small"
                          >
                            Start Basic Plan ($10/month)
                          </Button>
                          <Button 
                            variant="outlined" 
                            onClick={() => startFreeTrial('pro')}
                            size="small"
                          >
                            Start Pro Plan ($30/month)
                          </Button>
                        </Stack>
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          No active subscription. Start your free trial!
                        </Typography>
                        <Stack direction="row" spacing={2}>
                          <Button 
                            variant="contained" 
                            onClick={() => startFreeTrial('basic')}
                            size="small"
                          >
                            Start Basic Trial ($10/month)
                          </Button>
                          <Button 
                            variant="outlined" 
                            onClick={() => startFreeTrial('pro')}
                            size="small"
                          >
                            Start Pro Trial ($30/month)
                          </Button>
                        </Stack>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2">Loading subscription status...</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Google Connect Card */}
          {!isConnected && (
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Business sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Connect Your Google Business Profile
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Connect your Google Business Profile to start managing reviews
                  </Typography>
                  <GoogleConnect onConnect={handleGoogleConnect} loading={loading} />
                </CardContent>
              </Card>
            </Grid>
          )}

          {locations.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5" component="h2">
                      <LocationOn sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Your Locations ({locations.length})
                    </Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<LinkOff />}
                      onClick={handleDisconnect}
                      disabled={loading}
                    >
                      Disconnect Google Account
                    </Button>
                  </Box>
                  <LocationsList 
                    locations={locations} 
                    onLocationUpdate={handleLocationUpdate}
                  />
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
}

export default Dashboard;
